import React, { useState, useEffect } from 'react';
import { Briefcase, ChevronLeft, ChevronRight } from 'lucide-react';

export const Trainers = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isImageLoaded, setIsImageLoaded] = useState(true);
  const [nextIndex, setNextIndex] = useState(null);

  const trainers = [
    {
      name: 'Thatharik Pandiselvaraj',
      title: 'Film Editing & Post-Production Mentor',
      experience: '8+ years',
      image: 'https://customer-assets.emergentagent.com/job_coursecraft-26/artifacts/u6j8siuf_Thatharik%20Pandiselvaraj.jpeg',
      imageStyle: { objectPosition: 'center 15%', transform: 'scale(1.3)' }, // Zoom to face
      bio: 'Passionate storyteller specializing in Premiere Pro, After Effects, and Photoshop. Blends creativity with real-world techniques.'
    },
    {
      name: 'Gokulakrishnan',
      title: 'Video Editing & Social Media Content Mentor',
      experience: '4+ years',
      image: 'https://customer-assets.emergentagent.com/job_coursecraft-26/artifacts/huqkcfmj_GOKULAKRISHNAN%20P.jpeg',
      imageStyle: { objectPosition: 'center center', filter: 'brightness(1.1) contrast(1.05)', transform: 'scale(1.0)' }, // Zoom out to show full head
      bio: 'Digital strategist specializing in AVID Media Composer, Final Cut Pro, and DaVinci Resolve for professional-grade videos.'
    },
    {
      name: 'Giridharan',
      title: 'Video Editing and Color Grading Mentor',
      experience: '8+ years',
      image: 'https://customer-assets.emergentagent.com/job_coursecraft-26/artifacts/hrhv74ub_Giridharan.jpeg',
      imageStyle: { objectPosition: 'center 25%', transform: 'scale(1.3)' }, // Zoom to face
      bio: 'Expert in Avid Media Composer, DaVinci Resolve, and Final Cut Pro. Shapes confident, industry-ready editors.'
    },
    {
      name: 'Yeswanth',
      title: 'Video Editing & Post Production Mentor',
      experience: '3+ years',
      image: 'https://customer-assets.emergentagent.com/job_coursecraft-26/artifacts/o2wqc9yw_Yeswanth%20K.JPG',
      imageStyle: { objectPosition: 'center center' }, // New reframed photo
      bio: 'Visual creator specializing in Premiere Pro, After Effects, Photoshop, Illustrator, DaVinci Resolve, and Final Cut Pro.'
    }
  ];

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % trainers.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, trainers.length]);

  const goToSlide = (index) => {
    if (index === currentIndex) return;
    setIsImageLoaded(false);
    setNextIndex(index);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsAutoPlaying(false);
    }, 50);
  };

  const goToPrevious = () => {
    const newIndex = (currentIndex - 1 + trainers.length) % trainers.length;
    goToSlide(newIndex);
  };

  const goToNext = () => {
    const newIndex = (currentIndex + 1) % trainers.length;
    goToSlide(newIndex);
  };

  // Preload all images
  useEffect(() => {
    trainers.forEach((trainer) => {
      const img = new Image();
      img.src = trainer.image;
    });
  }, []);

  // Handle image load
  const handleImageLoad = () => {
    setIsImageLoaded(true);
    setNextIndex(null);
  };

  return (
    <section className="py-10 bg-white">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Section Header - Compact */}
          <div className="text-center mb-6">
            <div className="inline-block mb-2 px-4 py-1 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full">
              <span className="text-purple-700 font-semibold text-xs tracking-wide">OUR EXPERT TEAM</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
              Meet Your Expert Trainers
            </h2>
            <p className="text-gray-600 text-sm max-w-xl mx-auto">
              Learn from industry professionals with hands-on experience
            </p>
          </div>

          {/* Carousel Container */}
          <div className="relative">
            {/* Trainer Card */}
            <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl overflow-hidden shadow-lg">
              <div className="p-5 md:p-6">
                <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-start">
                  {/* Trainer Photo - Zoomed */}
                  <div className="flex-shrink-0">
                    <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-4 border-purple-200 shadow-lg relative">
                      {!isImageLoaded && (
                        <div className="absolute inset-0 bg-slate-200 animate-pulse flex items-center justify-center">
                          <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      <img 
                        src={trainers[currentIndex].image}
                        alt={trainers[currentIndex].name}
                        className={`w-full h-full object-cover transition-opacity duration-300 ${isImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        style={trainers[currentIndex].imageStyle || {}}
                        onLoad={handleImageLoad}
                        key={currentIndex}
                      />
                    </div>
                  </div>

                  {/* Trainer Info */}
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-1">
                      {trainers[currentIndex].name}
                    </h3>
                    <p className="text-purple-600 font-semibold text-sm mb-2">
                      {trainers[currentIndex].title}
                    </p>
                    <div className="flex items-center justify-center sm:justify-start gap-1.5 text-slate-600 text-xs mb-3">
                      <Briefcase className="h-3.5 w-3.5 text-orange-500" />
                      <span className="font-semibold">Experience: {trainers[currentIndex].experience}</span>
                    </div>
                    
                    {/* Bio */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3 border border-purple-100">
                      <p className="text-slate-700 leading-relaxed text-xs md:text-sm">
                        {trainers[currentIndex].bio}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={goToPrevious}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white rounded-full p-2 shadow-lg border border-slate-200 hover:bg-purple-50 hover:border-purple-300 transition-all duration-300"
              aria-label="Previous trainer"
            >
              <ChevronLeft className="h-5 w-5 text-purple-600" />
            </button>
            
            <button
              onClick={goToNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white rounded-full p-2 shadow-lg border border-slate-200 hover:bg-purple-50 hover:border-purple-300 transition-all duration-300"
              aria-label="Next trainer"
            >
              <ChevronRight className="h-5 w-5 text-purple-600" />
            </button>
          </div>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-2 mt-5">
            {trainers.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'w-8 bg-gradient-to-r from-purple-600 to-pink-600' 
                    : 'w-2 bg-slate-300 hover:bg-slate-400'
                }`}
                aria-label={`Go to trainer ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
